import { memo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'
import ReactMarkdown, { type Components } from 'react-markdown'

/**
 * Elements the assistant may never render, whatever the model writes. Raw
 * HTML is skipped outright; images and embeds would load third-party URLs.
 */
const DISALLOWED_ELEMENTS = [
  'img',
  'iframe',
  'video',
  'audio',
  'source',
  'object',
  'embed',
  'script',
  'style',
  'form',
  'input',
]

/**
 * An in-app path ("/case/123"), never another site, a protocol-relative URL
 * or a `javascript:` link.
 */
export const isInternalAssistantHref = (href: string | undefined | null) =>
  !!href &&
  href.startsWith('/') &&
  !href.startsWith('//') &&
  !href.includes('\\')

const components: Components = {
  // Internal links navigate in the app; anything else shows as plain text.
  a: ({ href, children }) =>
    isInternalAssistantHref(href) ? (
      <Link component={RouterLink} to={href as string} underline="hover">
        {children}
      </Link>
    ) : (
      <span>{children}</span>
    ),
}

/**
 * Markdown for the assistant's answers, locked down: no HTML, no images or
 * embeds, and only in-app links.
 */
const AssistantMarkdownBase = ({ text }: { text: string }) => (
  <ReactMarkdown
    skipHtml
    disallowedElements={DISALLOWED_ELEMENTS}
    unwrapDisallowed={false}
    components={components}
  >
    {text}
  </ReactMarkdown>
)

const AssistantMarkdown = memo(AssistantMarkdownBase)

export default AssistantMarkdown
